import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useUpdateResource } from "../../../hooks/useUpdateResource";
import useOpeningHoursForm, {
    OpeningHoursApiDay,
    OpeningHoursFormData,
} from "../../../hooks/useOpeningHoursForm";
import { useCategoryOpeningHours } from "../../../hooks/store/categoryOpeningHours";
import LoadingErrorState from "../../../components/ui/LoadingErrorState";
import OpeningHoursInputs from "../../../components/form/OpeningHoursInputs";
import LoadingButton from "../../../components/form/LoadingButton";
import SubmitButton from "../../../components/form/SubmitButton";
import SubTitle from "../../../components/ui/SubTitle";

const CategoryOpeningHours: React.FC = () => {
    const { t } = useTranslation();

    const { storeId, categoryId } = useParams<{
        storeId?: string;
        categoryId: string;
    }>();

    const numericStoreId = storeId
        ? Number(storeId)
        : undefined;

    const numericCategoryId = Number(categoryId);

    const {
        data: openingHours,
        isError,
        isLoading,
    } = useCategoryOpeningHours(
        numericStoreId,
        numericCategoryId
    );

    const {
        control,
        fields,
        watch,
        handleSubmit,
        activeDays,
        toggleDay,
        addPeriod,
        removePeriod,
        applied,
        handleApplyToggle,
        setOpeningHours,
        buildPayload,
    } = useOpeningHoursForm();

    const {
        loading,
        updateResource,
    } = useUpdateResource();

    useEffect(() => {
        if (!openingHours?.days) {
            return;
        }

        const apiDays: OpeningHoursApiDay[] =
            openingHours.days.map((day) => ({
                day: day.day,
                periods: day.periods.map((period) => ({
                    open: period.open,
                    close: period.close,
                })),
            }));

        setOpeningHours(apiDays);
    }, [
        openingHours,
        setOpeningHours,
    ]);

    const onSubmit = (
        data: OpeningHoursFormData,
    ) => {
        if (!openingHours || !numericCategoryId) {
            return;
        }

        const payload = buildPayload(data);

        const url = numericStoreId
            ? `category-opening-hours/current/${numericCategoryId}?storeId=${numericStoreId}`
            : `category-opening-hours/current/${numericCategoryId}`;

        updateResource(
            url,
            payload,
            {
                method: "PUT",
                successMessage: t("update_success"),
                invalidateQueries: [
                    "category-opening-hours",
                ],
            },
        );
    };

    return (
        <div>
            <LoadingErrorState
                isError={isError}
                isLoading={isLoading}
            />

            {openingHours && (
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="flex flex-col gap-6 w-full"
                >
                    <SubTitle
                        title={t("opening_hours")}
                    />

                    {openingHours.inheritSchedule === true && (
                        <p className="text-sm text-gray-500">
                            {t(
                                "category_uses_store_opening_hours"
                            )}
                        </p>
                    )}

                    <OpeningHoursInputs
                        control={control}
                        fields={fields}
                        watch={watch}
                        activeDays={activeDays}
                        toggleDay={toggleDay}
                        addPeriod={addPeriod}
                        removePeriod={removePeriod}
                        applied={applied}
                        handleApplyToggle={
                            handleApplyToggle
                        }
                    />

                    <div>
                        {loading ? (
                            <LoadingButton />
                        ) : (
                            <SubmitButton
                                label="update"
                            />
                        )}
                    </div>
                </form>
            )}
        </div>
    );
};

export default CategoryOpeningHours;